import { useEffect, useRef, useState } from 'react';
import { View, Text, TextInput, Pressable, StyleSheet } from 'react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY, withWeight } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';
import { useWebHover } from '../../hooks/useWebHover';
import { Button } from '../common/Button';
import { TextLink } from '../common/TextLink';
import { useDefensePlansStore } from '../../store/defensePlansStore';
import { useUIStore } from '../../store/uiStore';
import { scheduleDeferredCommit } from '../../utils/deferredCommit';
import { getErrorMessage } from '../../utils/errorHandler';
import { PLAN_PARTY_TYPES, partyDisplayName } from './planParties';
import type { PlanParty, PlanPartyType } from '../../types';

// Matched against askDisabledReason below to offer an inline "Share plan"
// fix right where the reason is shown, instead of sending the owner to the
// footer's Shared/Private control.
const PRIVATE_PLAN_ASK_REASON = 'Share this plan before asking for updates.';

// Owner-only edit for one party's own record, reached from
// PlanPartiesSection.web.tsx's roster row: type, organization, person,
// email (loaded on open, since contacts aren't subscribed state), and a
// remove that hides the party everywhere it's linked immediately and
// commits the real delete six seconds later — an undo toast
// (panel-system.md Phase 1), not a confirm, since the action is reversible
// for that window (src/utils/deferredCommit.ts).
//
// "Ask for an update" (Sept 2026 update loop) lives here too, not on its own
// sheet: the disabled reasons it can hit (no email saved, no defense linked)
// are either fixed in place on this same form or aren't this form's job to
// fix, and the party's email is already loaded here.

function Chip({
  label,
  selected,
  onPress,
  testID,
}: {
  label: string;
  selected: boolean;
  onPress: () => void;
  testID?: string;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole="radio"
      aria-checked={selected}
      accessibilityLabel={label}
      {...hoverProps}
      style={[
        styles.chip,
        CURSOR_POINTER,
        {
          borderColor: selected ? colors.action : colors.border,
          backgroundColor: selected ? colors.action : hovered ? colors.hoverSurface : 'transparent',
        },
      ]}>
      <Text style={[styles.chipLabel, { color: selected ? colors.actionOn : colors.text }]}>
        {label}
      </Text>
    </Pressable>
  );
}

export function DefensePlanPartyEditForm({
  planId,
  party,
  onClose,
  hideTitle = false,
}: {
  planId: string | null;
  party: PlanParty | null;
  onClose: () => void;
  /** The desktop companion's header already names this view and its
   *  object ("Edit party · Big Bear Fire Safe Council"), so the form's own
   *  title would repeat it. The phone sheet has no header and leaves it on. */
  hideTitle?: boolean;
}) {
  const { colors } = useTheme();
  const updateOwnerParty = useDefensePlansStore((s) => s.updateOwnerParty);
  const deleteOwnerParty = useDefensePlansStore((s) => s.deleteOwnerParty);
  const hideParty = useDefensePlansStore((s) => s.hideParty);
  const unhideParty = useDefensePlansStore((s) => s.unhideParty);
  const getPartyContactEmail = useDefensePlansStore((s) => s.getPartyContactEmail);
  const requestPlanUpdate = useDefensePlansStore((s) => s.requestPlanUpdate);
  const setVisibility = useDefensePlansStore((s) => s.setVisibility);
  const ownPlan = useDefensePlansStore((s) => s.ownPlan);
  const assignmentsByPlan = useDefensePlansStore((s) => s.assignmentsByPlan);

  const [type, setType] = useState<PlanPartyType>('homeowner');
  const [organization, setOrganization] = useState('');
  const [person, setPerson] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const emailInputRef = useRef<TextInput>(null);
  const [asking, setAsking] = useState(false);
  const [askNotice, setAskNotice] = useState<{ text: string; isError: boolean } | null>(null);

  // A fresh mount of this form IS "just opened" (the caller mounts it only
  // while a party is being edited), so this only needs to key off which
  // party — not a `visible` boolean.
  useEffect(() => {
    if (!party || !planId) return;
    setType(party.type);
    setOrganization(party.organization ?? '');
    setPerson(party.person ?? '');
    setEmail('');
    setAsking(false);
    setAskNotice(null);
    void getPartyContactEmail(planId, party.id).then((value) => {
      if (value) setEmail(value);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [party?.id, planId]);

  const canSave = organization.trim().length > 0 || person.trim().length > 0;

  // Disabled reasons for "Ask for an update" — checked in this order since
  // sharing the plan and linking a defense both need to happen before an
  // email is worth sending at all. A missing email is fixed right below on
  // this same form, so that one names it and points there rather than
  // sending the owner elsewhere.
  const assignmentCount =
    planId && party
      ? Object.values(assignmentsByPlan[planId] ?? {}).filter((a) => a.partyId === party.id).length
      : 0;
  const askDisabledReason: string | null =
    ownPlan?.visibility === 'private'
      ? PRIVATE_PLAN_ASK_REASON
      : assignmentCount === 0
        ? "This party isn't linked to a defense yet."
        : email.trim().length === 0
          ? 'Add an email below, then ask.'
          : null;

  const handleAskForUpdate = async () => {
    if (!planId || !party || askDisabledReason) return;
    setAsking(true);
    setAskNotice(null);
    try {
      const result = await requestPlanUpdate(planId, party.id);
      if (result.sent) {
        const text = `Asked. ${result.taskCount} task${result.taskCount === 1 ? '' : 's'} included.`;
        setAskNotice({ text, isError: false });
        useUIStore.getState().showToast({ message: text });
        return;
      }
      switch (result.reason) {
        case 'no-email':
          // The button is disabled whenever the local email field reads
          // empty, so this path means an email was typed but never saved —
          // point at the field rather than dead-ending.
          setAskNotice({ text: 'Save an email for this party first.', isError: true });
          emailInputRef.current?.focus();
          break;
        case 'no-assignments':
          setAskNotice({ text: "This party isn't linked to a defense yet.", isError: true });
          break;
        case 'suppressed':
          setAskNotice({ text: 'That email has opted out of REDI mail.', isError: true });
          break;
        default:
          setAskNotice({ text: "That didn't send. Try again.", isError: true });
      }
    } catch (err) {
      // requestPlanUpdate throws failed-precondition for the ask cooldown
      // (server message names the date it can be asked again) — shown as
      // the message itself rather than a generic failure.
      const code = (err as { code?: string } | null)?.code;
      const message = (err as { message?: string } | null)?.message;
      setAskNotice({
        text: code === 'functions/failed-precondition' && message ? message : getErrorMessage(err),
        isError: true,
      });
    } finally {
      setAsking(false);
    }
  };

  // ownPlan is always the signed-in user's own plan (this form only opens
  // from editing your own plan's roster), so ownership is already implied —
  // same mirrored condition the panel's Shared/Private control relies on.
  const handleSharePlan = async () => {
    if (!ownPlan) return;
    const saved = await setVisibility('shared');
    useUIStore
      .getState()
      .showToast({ message: saved ? 'Plan shared' : "Couldn't change who can see this plan" });
  };

  const handleSave = async () => {
    if (!planId || !party || !canSave) return;
    setSaving(true);
    try {
      await updateOwnerParty(
        planId,
        party.id,
        { type, organization: organization.trim(), person: person.trim() },
        email.trim()
      );
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // Hides the party (and its assignments — see the DefensePlanPanel/Viewer
  // `parties`/`assignments` derivation) immediately, closes this form, and
  // commits the real delete after 6 seconds unless Undo cancels it —
  // replaces the "Remove this party?" confirm (panel-system.md Phase 1,
  // rule 6: an undo toast instead of a confirm for a reversible action).
  const handleDelete = () => {
    if (!planId || !party) return;
    const message = `Removed ${partyDisplayName(party)}`;
    onClose();
    scheduleDeferredCommit({
      key: `party-${party.id}`,
      message,
      commit: async () => {
        await deleteOwnerParty(planId, party.id);
        // deleteOwnerParty never rejects (it rolls itself back on failure
        // and records saveError instead) — this cleans up the hide flag
        // either way: a no-op once the party is really gone, or a genuine
        // restore alongside the store's own rollback if the write failed.
        unhideParty(party.id);
      },
      onHide: () => hideParty(party.id),
      onUndo: () => unhideParty(party.id),
    });
  };

  return (
    <View style={[styles.content, hideTitle && styles.contentCompanion]}>
      {!hideTitle && <Text style={[styles.title, { color: colors.text }]}>Edit party</Text>}

      <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Type</Text>
      <View style={styles.typeChips}>
        {PLAN_PARTY_TYPES.map((t) => (
          <Chip
            key={t.key}
            label={t.label}
            selected={type === t.key}
            onPress={() => setType(t.key)}
            testID={`party-edit-type-${t.key}`}
          />
        ))}
      </View>

      <Text style={[styles.sectionLabel, { color: colors.textSecondary, marginTop: SPACING.sm }]}>
        Organization
      </Text>
      <TextInput
        value={organization}
        onChangeText={setOrganization}
        placeholder="e.g. Big Bear Fire Safe Council"
        placeholderTextColor={colors.textTertiary}
        testID="party-edit-organization"
        accessibilityLabel="Organization"
        style={[styles.input, { color: colors.text, borderColor: colors.border }]}
      />
      <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Person</Text>
      <TextInput
        value={person}
        onChangeText={setPerson}
        placeholder="e.g. Maria Lopez"
        placeholderTextColor={colors.textTertiary}
        testID="party-edit-person"
        accessibilityLabel="Person"
        style={[styles.input, { color: colors.text, borderColor: colors.border }]}
      />
      <Text style={[styles.sectionLabel, { color: colors.textSecondary }]}>Email</Text>
      <TextInput
        ref={emailInputRef}
        value={email}
        onChangeText={setEmail}
        placeholderTextColor={colors.textTertiary}
        keyboardType="email-address"
        autoCapitalize="none"
        testID="party-edit-email"
        accessibilityLabel="Email"
        accessibilityHint="Only you can see this"
        style={[styles.input, { color: colors.text, borderColor: colors.border }]}
      />
      <Text style={[styles.helperText, { color: colors.textTertiary }]}>Only you can see this</Text>

      <View style={styles.askBlock}>
        <Button
          title="Ask for an update"
          onPress={() => void handleAskForUpdate()}
          variant="secondary"
          size="sm"
          disabled={!!askDisabledReason}
          loading={asking}
          testID="plan-party-ask-update"
        />
        {askNotice ? (
          <Text
            style={[
              styles.askHint,
              { color: askNotice.isError ? colors.danger : colors.textSecondary },
            ]}
            testID="plan-party-ask-update-notice">
            {askNotice.text}
          </Text>
        ) : askDisabledReason ? (
          <View style={styles.disabledReasonRow}>
            <Text style={[styles.askHint, { color: colors.textTertiary }]}>
              {askDisabledReason}
            </Text>
            {askDisabledReason === PRIVATE_PLAN_ASK_REASON && (
              <TextLink
                label="Share plan"
                onPress={handleSharePlan}
                textStyle={styles.askHint}
                testID="plan-party-ask-share"
              />
            )}
          </View>
        ) : null}
      </View>

      <View style={styles.actions}>
        <Button
          title="Remove party"
          onPress={handleDelete}
          variant="danger"
          size="sm"
          testID="party-edit-delete"
        />
        <Button
          title="Save"
          onPress={() => void handleSave()}
          variant="primary"
          size="sm"
          disabled={!canSave}
          loading={saving}
          testID="party-edit-save"
        />
      </View>

      {/* Scroll sentinel: Maestro flows assert this is reachable. */}
      <View testID="party-edit-sheet-end" style={styles.scrollSentinel} />
    </View>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  // In the desktop companion: the header's side padding, so the body shares
  // its left edge (the phone sheet keeps the sheet padding above).
  contentCompanion: {
    paddingHorizontal: SPACING.md,
    paddingTop: SPACING.md,
  },
  title: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.md,
  },
  sectionLabel: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.xxs,
  },
  typeChips: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: SPACING.xs,
    marginBottom: SPACING.sm,
  },
  chip: {
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.xxs,
    borderRadius: BORDER_RADIUS.full,
    borderWidth: 1,
  },
  chipLabel: {
    ...withWeight(TYPOGRAPHY.caption, 'medium'),
  },
  input: {
    ...TYPOGRAPHY.body,
    borderWidth: 1,
    borderRadius: BORDER_RADIUS.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
    marginBottom: SPACING.xs,
  },
  helperText: {
    ...TYPOGRAPHY.caption,
    marginTop: -SPACING.xxs,
    marginBottom: SPACING.xs,
  },
  askBlock: {
    marginTop: SPACING.xs,
    gap: SPACING.xxs,
    alignItems: 'flex-start',
  },
  askHint: {
    ...TYPOGRAPHY.caption,
  },
  disabledReasonRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: SPACING.xs,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: SPACING.md,
  },
  scrollSentinel: {
    height: 1,
  },
});
